import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EnvironmentConfig, ENVIRONMENT_CONFIG } from './config.model';
import { VERSION } from './version';

@Injectable({
    providedIn: 'root'
})
export class ConfigService {
    private readonly config = inject(ENVIRONMENT_CONFIG);
    private readonly platformId = inject(PLATFORM_ID);

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            window.APP_NAME = this.config.appName;
        }
    }

    get coreAppUrl(): string {
        return this.config.coreAppUrl;
    }

    get adminAppUrl(): string {
        return this.config.adminAppUrl;
    }

    get apiUrl(): string {
        return this.config.apiUrl;
    }

    get authApiUrl(): string {
        return this.config.authApiUrl;
    }

    get blogApiUrl(): string {
        return this.config.blogApiUrl;
    }

    get isProduction(): boolean {
        return this.config.production;
    }

    get isDevelopment(): boolean {
        return !this.config.production;
    }

    get productionOnly(): boolean {
        // Default to true in production, false in development
        return this.config.productionOnly ?? this.config.production;
    }

    get enableDebug(): boolean {
        return this.config.enableDebug;
    }

    get version(): string {
        return VERSION || this.config.version;
    }

    get appName(): string {
        return this.config.appName;
    }

    get features() {
        return this.config.features;
    }

    get googleAnalyticsId(): string {
        return this.config.googleAnalyticsId || '';
    }

    // Helper methods for common API endpoints
    getAuthEndpoint(path = ''): string {
        return `${this.authApiUrl}${path.startsWith('/') ? path : `/${path}`}`;
    }

    getBlogEndpoint(path: string | null = ''): string {
        if (path === null) {
            return this.blogApiUrl;
        }

        return `${this.blogApiUrl}${path.startsWith('/') ? path : `/${path}`}`;
    }

    getApiEndpoint(path = ''): string {
        return `${this.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
    }

    // Feature flag helpers
    isFeatureEnabled(feature: keyof EnvironmentConfig['features']): boolean {
        return this.features[feature];
    }

    // Logging helper
    log(message: string, ...args: any[]): void {
        if (this.enableDebug && this.isFeatureEnabled('enableLogging')) {
            console.log(`[${this.appName}] ${message}`, ...args);
        }
    }

    // Error logging helper
    logError(message: string, error?: any): void {
        if (this.enableDebug) {
            console.error(`[${this.appName}] ERROR: ${message}`, error);
        }
    }

    // Get full configuration (for debugging)
    getConfig(): EnvironmentConfig {
        if (this.enableDebug) {
            return { ...this.config };
        }
        throw new Error('Configuration access not allowed in production mode');
    }
}
